"use client";

import { recData } from "@/lib/rec-client";
import { landCardBits } from "@/lib/land-facts";
import { signedPct } from "@/lib/format/change";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AlertTriangle, Building2, Heart, Copy as CopyIcon, FileText, FileWarning, Eye, Users } from "lucide-react";
import { useT, useLocale } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";
import { haptic } from "@/lib/haptics";
import SaveCelebration from "@/components/SaveCelebration";
import { formatPrice, currencySign } from "@/lib/api";
import { roundEstimate } from "@/lib/estimate";
import { BrandMark } from "@/lib/brand-mark";
import { CARD_SIZES, upgradeCardImage } from "@/lib/listing-image";
import { useWatchlist } from "@/lib/use-watchlist";
import { useViewed, changeSinceViewed } from "@/lib/viewed";
import { isSeparate, kindNounKey } from "@/lib/property-kinds";
import { maybeAgent } from "@/lib/seller-mark";
import { CARD_LABEL, cardLabels, deedLaterFromText } from "@/lib/text-facts";
import { hasDeedConflict } from "@/lib/deed-claim";
import { flatSellersText } from "@/lib/flat-sellers";
import type { ListingCard } from "@/types";
import CardPhoto from "@/components/CardPhoto";
import { preloadDetailBody } from "./detail-body-loader";
import { CURRENT_CARD } from "@/lib/ui-classes";

interface Props {
  /** The card whose details are open in the docked pane: an ink outline keeps it findable after the columns reflow (feedback ticket 10, 2026-10-14; ink, not indigo, since 2026-10-21). */
  active?: boolean;
  listing: ListingCard;
  /** Open the detail panel for this listing. Called on click / Enter /
   *  Space anywhere on the card. */
  onOpen: (listing: ListingCard) => void;
  /** Optional extra line rendered under the price row. Used by the
   *  watchlist to surface the since-saved price change (the thing that
   *  makes a tracked listing differ from a plain browse card). */
  priceNote?: React.ReactNode;
  /** No viewed mark (the history's viewed list: everything there is). */
  hideViewed?: boolean;
  /** Sale/rent badge. Grids with a pinned transaction mode
   *  pass false - repeating the active filter on every card is noise.
   *  Defaults true for mixed surfaces (watchlist, alert feeds). */
  showTransaction?: boolean;
  /** The place the whole grid is about (a landing page's place): the
   *  card stops repeating it and shows the residence instead. */
  hidePlace?: string | null;
  /** "grid" (default) is the vertical photo card; "row" lays the same
   *  card horizontally (photo left, facts right) for the list view. */
  variant?: "grid" | "row";
  /** Row variant next to a docked detail pane: half the width, so the
   *  numbers rail hides and the photo narrows. */
  dense?: boolean;
  /** Above-the-fold card: load its photo eagerly with high fetch priority
   *  so the browse page's LCP is the first photo, not a lazy one. */
  priority?: boolean;
  /** Apply a filter from a chip (row variant). Absent on surfaces with
   *  no browse behind them, where the chips stay labels. */
  onFilter?: (params: Record<string, string>) => void;
  /** The listing's own page (/az/elan/...). When set, the price is a real
   *  link to it: a plain click still opens the drawer, a new-tab click
   *  opens the page, and crawlers can follow it. */
  href?: string;
  /** No heart on the photo: a shared list's card, where the list is the
   *  saved thing and the vote row under it is the action (the heart read
   *  as a second vote, design critique 2026-10-23). */
  hideSave?: boolean;
  /** The facts the search asked for (the URL's `fact`): their labels
   *  come first among the card's grey labels. */
  activeFacts?: readonly string[];
  /** A recommended card: its request id and position, carried as
   *  data-rec / data-pos for the events of the drawer it opens
   *  (lib/rec-client). */
  rec?: string | null;
  pos?: number | null;
  /** A control in the photo's bottom-right corner, mirroring the heart
   *  (the recommendation's "..." menu, components/listings/RecCardMenu). */
  menu?: React.ReactNode;
}

// Verdict chip per the de-slop board (7c): tinted pastel pill, three
// buckets (deal / fair / over) instead of seven saturated tiers - the
// exact % carries the precision, the tint only carries the direction.
// Literal hex (not theme-swapped): the chip sits over photos and must
// read identically in both themes.
const TIER_BUCKET: Record<string, "deal" | "fair" | "over" | "lux"> = {
  exceptional_deal: "deal",
  great_deal: "deal",
  good_deal: "deal",
  fair_price: "fair",
  slightly_overpriced: "over",
  overpriced: "over",
  heavily_overpriced: "over",
  // A premium segment the model cannot price: its own neutral chip, never
  // "fair" (a +189% listing once read as Ortalama on the card).
  luxury_segment: "lux",
};
// The chip sits ON the photo, so both modes need an opaque fill: the
// old 15%-alpha dark tints went glassy and vanished over busy images
// (owner, 2026-09-20). Dark = deep solid hue + pale text; light = pale
// solid tint + deep text, with a hairline ring for bright photos.
const BUCKET_STYLE: Record<"deal" | "fair" | "over" | "lux", string> = {
  deal: "bg-[#ecfdf5] text-[#047857] ring-[#047857]/15 dark:bg-[#065f46] dark:text-[#d1fae5] dark:ring-white/10",
  fair: "bg-[#fffbeb] text-[#b45309] ring-[#b45309]/15 dark:bg-[#78350f] dark:text-[#fef3c7] dark:ring-white/10",
  over: "bg-[#fff1f2] text-[#be123c] ring-[#be123c]/15 dark:bg-[#9f1239] dark:text-[#ffe4e6] dark:ring-white/10",
  lux: "bg-[#f1f5f9] text-[#334155] ring-[#334155]/15 dark:bg-[#334155] dark:text-[#e2e8f0] dark:ring-white/10",
};

// Verdict chip: the signed % with the word for what it measures
// ("+46% above fair"; a bare "+46%" carried no word, UI critique
// 2026-10-18), or the "fair price" words (a signed 0% reads odd),
// tinted by bucket. Exported so the listing drawer and the Telegram
// Mini App say the verdict in these same words and whole percents
// (one verdict language, design critique 2026-10-23). On a narrow card
// (a phone's two columns, under 15rem: the photo is an @container) the
// % keeps a one-word reading ("+54% baha") so the badge never runs under
// the top-right ones (owner, 2026-10-30); the whole words stay in the
// title, and truncation is the last resort.
export function VerdictBadge({ tier, pct, fairLabel, dealLabel, overLabel, luxLabel, dealShort, overShort }: {
  tier: string | null;
  pct: number | null;
  fairLabel: string;
  dealLabel: string;
  overLabel: string;
  luxLabel?: string;
  dealShort?: string;
  overShort?: string;
}) {
  if (tier === null || pct === null) return null;
  const bucket = TIER_BUCKET[tier];
  if (!bucket) return null;
  const text =
    bucket === "fair"
      ? fairLabel
      : bucket === "lux"
        ? (luxLabel ?? "Premium")
        : `${signedPct(pct, 0)} ${bucket === "deal" ? dealLabel : overLabel}`;
  const short = bucket === "deal" ? dealShort : bucket === "over" ? overShort : undefined;
  return (
    <span title={text} className={`inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-xs font-bold ring-1 ${BUCKET_STYLE[bucket]}`}>
      {short ? (
        <>
          <span className="truncate @max-[15rem]:hidden">{text}</span>
          <span className="hidden truncate @max-[15rem]:inline">{`${signedPct(pct, 0)} ${short}`}</span>
        </>
      ) : (
        <span className="truncate">{text}</span>
      )}
    </span>
  );
}

// "bu gün" / "dünən" / "{n} gün əvvəl", empty beyond 30 days (a stale
// date on every card is noise, freshness is the only signal we want).
function recencyLabel(
  iso: string | null,
  t: ReturnType<typeof useT>,
  short = false,
): string | null {
  if (!iso) return null;
  // The API's timestamps are UTC without a zone ("2026-10-12T08:00:00").
  // Read bare, the browser takes them as its own local time (Baku, 4 h
  // off the server's UTC), so a card rendered on the server said "9 days"
  // and the same card in the browser "10 days": React threw #418 on the
  // developer pages (2026-10-22). Read them as UTC on both sides.
  const at = iso.endsWith("Z") || /[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`;
  const days = Math.floor((Date.now() - new Date(at).getTime()) / 86_400_000);
  if (days < 0 || days > 30) return null;
  if (days === 0) return t("listings.card.when.today");
  if (days === 1) return t("listings.card.when.yesterday");
  return t(short ? "listings.card.when.days_short" : "listings.card.when.days").replace("{n}", String(days));
}

/** "140k" for the large sums of a take-over line, so two of them fit one
 *  row of a grid card; monthly payments stay in full. */
function compactAzn(n: number): string {
  return n >= 10_000 ? `${Math.round(n / 1000)}k` : formatPrice(n);
}

const NO_FACTS: readonly string[] = [];

interface QuietLabel { text: string; warn: boolean }

/**
 * A grid card's quiet line (2026-10-29): its label words first (one on a
 * phone, two on a wider card), then AZN/m² (phones; wider cards print it
 * beside the price) and the freshness as one run that may end in an
 * ellipsis, as before. A label is never cut: one that does not fit the
 * line whole is dropped, the lowest first.
 */
function QuietLine({ labels, ppm, rest, title }: {
  labels: QuietLabel[];
  ppm: string | null;
  rest: React.ReactNode;
  title?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [fit, setFit] = useState(labels.length);
  // The labels do not shrink and the tail does: anything wider than the
  // line is the labels, one too many. Drop the last and look again.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && fit > 0 && el.scrollWidth > el.clientWidth + 1) setFit((n) => Math.max(0, n - 1));
  }, [fit]);
  // A wider card (a turned phone, a resized window) may hold them again.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let width = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (Math.abs(el.clientWidth - width) < 2) return;
      width = el.clientWidth;
      setFit(labels.length);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [labels.length]);
  const shown = labels.slice(0, fit);
  const dot = <span aria-hidden className="text-gray-400">·</span>;
  const tail = ppm || rest;
  return (
    <span ref={ref} title={title} className="mt-auto flex min-w-0 items-baseline gap-x-1 overflow-hidden whitespace-nowrap text-xs tabular-nums text-gray-500">
      {shown.map((l, i) => (
        <span key={l.text} className={`${i > 0 ? "hidden sm:inline-flex" : "inline-flex"} shrink-0 items-baseline gap-x-1`}>
          {i > 0 && dot}
          <span className={`font-semibold ${l.warn ? "text-amber-700" : "text-gray-600"}`}>{l.text}</span>
        </span>
      ))}
      {tail && (
        // Wider cards print the AZN/m² beside the price: without a
        // freshness the tail has nothing to show there.
        <span className={`min-w-0 truncate ${rest ? "sm:min-w-[3.5rem]" : "sm:hidden"}`}>
          {shown.length > 0 && <span aria-hidden className="mr-1 text-gray-400">·</span>}
          {ppm && (
            <span className="sm:hidden">
              {ppm}
              {rest ? " · " : ""}
            </span>
          )}
          {rest}
        </span>
      )}
    </span>
  );
}

export default function ListingBrowseCard({ listing, onOpen, priceNote, hideViewed = false, showTransaction = true, variant = "grid", dense = false, priority = false, onFilter, href, hidePlace = null, active = false, hideSave = false, activeFacts = NO_FACTS, rec = null, pos = null, menu = null }: Props) {
  const t = useT();
  const { signedIn, has, toggle } = useWatchlist();
  // Non-zero after the user SAVES (not unsaves) - keys the celebration.
  const [saveBurst, setSaveBurst] = useState(0);
  // React reuses card instances when the grid re-renders (page flip,
  // filter change) - a leftover burst from a previous listing must not
  // replay its celebration on whatever listing lands in this slot.
  useEffect(() => {
    setSaveBurst(0);
  }, [listing.id]);
  const watched = has(listing.id);
  const { locale } = useLocale();
  // Opened before (this account / device): a "viewed" chip, carrying
  // the price move since that view when there is one.
  const viewedEntry = useViewed().get(listing.id);
  const seen = hideViewed ? undefined : viewedEntry;

  const {
    price, currency, ppm: rawPpm,
    room_count, area_m2, floor, total_floors, land_area_sot,
    category, leased,
    thumbnail_url,
    location_name, district, metro, sub_location,
    ai_overpriced_pct, ai_verdict_tier,
    gross_yield_pct, projected_rent,
    dup_group_size, flat_sellers,
    has_bill_of_sale, first_seen_at,
    has_repair, has_mortgage, mortgage_kind, mortgage_terms, effective_price, building_type, complex_name,
    photos_count, ai_predicted_price,
    land_facts, city_name, deed_area_m2,
    property_kind, street_facing,
  } = listing;
  // The site ticks a deed the text says is not there yet (lib/deed-claim):
  // the flag only, the seller's words are never shown.
  const deedConflict = hasDeedConflict(listing);
  // A commercial space or a garage (2026-10-21) reads by what it is, its
  // size, floor and frontage: no rooms, build, mortgage or valuation.
  const separate = isSeparate(category);
  const kindNoun = separate ? t(kindNounKey(category, property_kind)) : null;
  const streetLabel = separate && street_facing ? t("listings.card.street_facing") : null;

  // Location display: prefer district -> metro -> sub_location -> location_name
  const locationLabel = district ?? metro ?? sub_location ?? location_name ?? t("common.na");
  // Photo alt says what the photo is of ("2 otaqlı mənzil, 65 m²,
  // İnşaatçılar, 150 000 AZN"): "2 İnşaatçılar" repeated on every card
  // told search engines and screen readers nothing (SEO audit, 2026-10-07).
  const photoAlt = [
    separate ? kindNoun
    : category === "land"
      ? land_area_sot !== null && t("listings.card.sot").replace("{n}", String(land_area_sot))
      : room_count !== null &&
        t(category === "house" ? "home.spot.title_house" : "home.spot.title").replace("{n}", String(room_count)),
    category !== "land" && area_m2 !== null && t("listings.card.area").replace("{n}", String(Math.round(area_m2))),
    complex_name ?? (district ?? metro ?? sub_location ?? location_name ?? city_name),
    price !== null && `${formatPrice(price)} ${currency === "USD" ? "$" : "AZN"}`,
  ].filter(Boolean).join(", ");

  // A photo filter's result shows the photo it matched (the kitchen for
  // "kitchen", the sea for "sea view"), else the cover (owner, 2026-10-22).
  const shownPhoto = listing.match_photo ?? thumbnail_url;
  // The API's resized copies (8-20 KB WebP, a srcset per card width)
  // for the sites it fetches, the site's own card size for the rest
  // (lib/listing-image cardPhoto).
  const cardImage = upgradeCardImage(shownPhoto);

  // Floor label: "6/15 mərtəbə"
  const floorLabel =
    floor !== null && total_floors !== null
      ? t("listings.card.floor")
          .replace("{floor}", String(floor))
          .replace("{total}", String(total_floors))
      : floor !== null
      ? `${floor} ${locale === "ru" ? "эт." : locale === "en" ? "fl." : "mərtəbə"}`
      : null;

  // Land is priced per sot (100 m²), like the land map and the filters
  // (owner, 2026-10-07: "we still show per square meter on land").
  const isLand = category === "land";
  // Under 0.5 AZN/m² (a huge hall let for little) it would read "0 AZN/m²".
  const ppm: number | null = isLand
    ? (price && land_area_sot ? Math.round(price / land_area_sot) : null)
    : rawPpm != null && rawPpm >= 0.5 ? rawPpm : null;
  const ppmKey = isLand ? "listings.card.pps" : "listings.card.ppm";
  const ppmUnit = isLand ? t("listings.card.pps_unit") : "AZN/m²";

  const transactionLabel = leased ? t("listings.card.rent") : t("listings.card.sale");

  // Show AI verdict only for apartment / house - other categories have null pct already.
  const hasVerdict = ai_overpriced_pct !== null && ai_verdict_tier !== null;

  // The photo's top-right pills (a grid card stacks them beside the
  // verdict, a row card places them itself). The transaction badge is
  // suppressed when the grid's transaction filter already pins one mode;
  // near-black, not indigo (quiet UI): a sale chip sat on every photo of
  // a mixed grid. Literal hex: it sits on the photo in both themes.
  const transactionChip = (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
      leased ? "bg-[#b45309] text-white" : "bg-[#111827]/85 text-white"
    }`}>
      {transactionLabel}
    </span>
  );
  // An eye, not a word (owner, 2026-10-13); the price move since the view
  // rides beside it. The words stay for the tooltip and screen readers.
  const seenMoved = seen ? changeSinceViewed(listing.price, seen.price) : null;
  const seenChip = seen ? (
    <span
      title={seenMoved == null ? t("viewed.chip") : `${t("viewed.chip.since")} ${signedPct(seenMoved)}`}
      className={`inline-flex h-6 items-center gap-1 rounded-full bg-black/60 text-xs font-semibold text-white ${seenMoved == null ? "w-6 justify-center" : "px-2"}`}
    >
      <Eye className="h-3.5 w-3.5" aria-hidden />
      <span className="sr-only">{seenMoved == null ? t("viewed.chip") : t("viewed.chip.since")}</span>
      {seenMoved != null && (
        <span className={`tabular-nums ${seenMoved < 0 ? "text-emerald-300" : "text-rose-300"}`}>{signedPct(seenMoved)}</span>
      )}
    </span>
  ) : null;
  // The same unit posted several times (2026-10-29): a neutral pill like
  // the viewed eye, the number and the copy mark; the words are its title.
  const dupWords = dup_group_size != null && dup_group_size > 1
    ? `${t("listings.card.dup.badge").replace("{n}", String(dup_group_size))}. ${t("listings.card.dup.tooltip").replace("{n}", String(dup_group_size))}`
    : null;
  const dupChip = variant === "grid" && dupWords ? (
    <span title={dupWords} aria-label={dupWords} className="inline-flex h-6 items-center gap-1 rounded-full bg-black/60 px-2 text-xs font-semibold tabular-nums text-white">
      <CopyIcon className="h-3.5 w-3.5" aria-hidden />
      {dup_group_size}
    </span>
  ) : null;
  // Who lists the flat now (mandate plan 2.C.3): one pill per flat in
  // the copies pill's treatment. In the grid it sits at the photo's
  // bottom right, never on the verdict chip's row: beside the chip it
  // truncated "Ədalətli qiymət" to "Ədalə..." at 390 px (2026-11-04).
  const sellersWords = flatSellersText(flat_sellers, leased, locale, t);
  const sellersChip = sellersWords ? (
    <span
      title={`${sellersWords}. ${t("listings.flat_sellers.hint")}`}
      className="inline-flex h-6 max-w-[11rem] items-center gap-1 rounded-full bg-black/60 px-2 text-xs font-semibold tabular-nums text-white"
    >
      <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="truncate">{sellersWords}</span>
    </span>
  ) : null;

  // Show yield badge only for for-sale apartment/house listings with a non-null yield.
  const isForSale = leased === false || leased === null;
  const isResidential = category === "apartment" || category === "house";
  const hasYield =
    gross_yield_pct != null &&
    isForSale &&
    isResidential;

  // Facts as ONE dot-separated line (board 7c): "2 otaqlı · 68 m² ·
  // 4/9 · Yasamal". Houses swap the floor slot for the land plot.
  const factsLine = [
    // A shop or a garage leads with what it is ("Ofis · 120 m² · 1/9").
    kindNoun,
    !isLand && !separate && room_count !== null &&
      t("listings.card.rooms").replace("{n}", String(room_count)),
    !isLand && area_m2 !== null && (deed_area_m2
      ? t("listings.card.area_deed").replace("{n}", String(Math.round(area_m2))).replace("{d}", String(Math.round(deed_area_m2)))
      : t("listings.card.area").replace("{n}", String(Math.round(area_m2)))),
    isLand || category === "garage" ? null : floor !== null && total_floors !== null
      ? `${floor}/${total_floors}`
      : floorLabel,
    (category === "house" || category === "land") && land_area_sot !== null &&
      t("listings.card.sot").replace("{n}", String(land_area_sot)),
    // On a page about this place every card said its name (owner,
    // 2026-10-02): say the residence, or nothing, instead.
    hidePlace && [district, metro, sub_location, location_name].includes(hidePlace)
      ? complex_name ?? null
      // A facts line is not a form: an unknown place is left out rather
      // than printed as "N/A" (most land plots carry no place).
      : (district ?? metro ?? sub_location ?? location_name ?? city_name ?? null),
  ].filter(Boolean).join(" · ");

  // Row variant facts: deed, repair, mortgage, building age and the
  // complex as readable chips (the grid keeps its one gray line).
  const recency = recencyLabel(first_seen_at, t);
  // Each chip is the filter it names, so tapping "Təmirli" narrows the
  // browse to renovated flats (owner, 2026-09-22: "should we make the
  // extra amenities clickable so you actually click on them and filter
  // by them, with the haptic feedback"). A chip with no filter behind it
  // stays a plain label.
  // ``warn``: a chip the buyer must not miss (the deed holds less than
  // the listed area), amber like the findings' warnings.
  type Chip = { label: string; filter?: Record<string, string>; warn?: boolean };
  const rowChips: Chip[] = ([
    // Faces the street: a plain label (no filter behind it).
    streetLabel && { label: streetLabel },
    !isLand && has_repair === true && { label: t("listings.filter.has_repair"), filter: { has_repair: "true" } },
    !isLand && !separate && has_mortgage === true && { label: t("listings.filter.has_mortgage"), filter: { has_mortgage: "true" } },
    !isLand && !separate && mortgage_kind === "takeover" && {
      label: `${t("listings.filter.mortgage_takeover")}${mortgage_terms?.rate_pct ? ` · ${mortgage_terms.rate_pct}%` : ""}`,
      filter: { mortgage_kind: "takeover" },
    },
    !isLand && !!deed_area_m2 && {
      label: t("listings.card.deed_chip").replace("{d}", String(Math.round(deed_area_m2))),
      filter: { deed_gap: "yes" },
      warn: true,
    },
    // The site ticks a deed the text says is not there yet: a warning,
    // never the deed mark (lib/deed-claim).
    deedConflict && { label: t("deed_claim.chip"), warn: true },
    !isLand && !separate && building_type === "new" && { label: t("listings.card.new_build"), filter: { building_type: "new" } },
    !isLand && !separate && building_type === "old" && { label: t("listings.card.old_build"), filter: { building_type: "old" } },
    complex_name && {
      label: complex_name,
      filter: listing.complex_slug ? { complex_slug: listing.complex_slug } : undefined,
    },
    // Labelled owner, but the weekly scoring or the seller's book says
    // agent (lib/seller-mark, services/seller_trust): a plain label, not a filter.
    maybeAgent(listing) && { label: t("listings.card.maybe_agent"), warn: true },
  ] as (Chip | false | null | "" | undefined)[]).filter((x): x is Chip => Boolean(x));

  // The quiet line: AZN/m², kupça, yield, freshness - metadata in
  // gray-400, never competing with the price.
  // The grid has no chip row: the quiet line opens with the mark, amber
  // like every warning, so the card's width cuts the date, never the mark.
  const quietLine = [
    ...(isLand ? landCardBits(land_facts, t) : []),
    // The grid card has no chip row: the frontage rides the quiet line.
    variant === "grid" ? streetLabel : null,
    hasYield && `${t("listings.card.yield")} ${Math.round(gross_yield_pct!)}%`,
    recencyLabel(first_seen_at, t),
  ].filter(Boolean).join(" · ");
  // The title deed is a document icon, not a word (owner, 2026-09-21:
  // "I still see the title deed as text under the cards, I want it to
  // be an icon"). The label stays as the tooltip and the accessible
  // name, so nothing is lost for a screen reader or on hover.
  // A flat sold with its loan shows what the buyer pays now and per
  // month, the facts such a buyer reads first (ticket 9, 2026-10-14).
  const takeover = !isLand && (mortgage_kind === "takeover" || effective_price != null);
  const terms = takeover && mortgage_terms
    && (mortgage_terms.initial || mortgage_terms.monthly || mortgage_terms.remaining)
    ? mortgage_terms
    : null;
  // A take-over posted at its initial payment is valued, sorted and
  // filtered at its stated total (backend services/effective_price): the
  // line leads with that total, so the verdict and the AZN/m² beside a
  // small posted price read right.
  const totalPair: Array<[TranslationKey, number]> = takeover && effective_price
    ? [["listings.mortgage.total", effective_price]]
    : [];
  // The sums stay on the text under the facts (owner, 2026-10-15: over
  // some photos the numbers could not be read), in the facts' gray, not
  // indigo ("too bluish", 2026-10-16); only the rate sits on the photo
  // as a small mark. When the listed price is the take-over payment
  // itself (price_basis "initial"), repeating it as the down payment
  // says nothing: the line leads with the bank debt. A take-over whose
  // text states no numbers says so, in the same slot, so the rows of a
  // grid line up and the slot never reads as a gap.
  const termPairs = [...totalPair, ...(terms
    ? (terms.price_basis === "initial"
        ? [["listings.mortgage.remaining", terms.remaining], ["listings.mortgage.monthly", terms.monthly]]
        : [["listings.mortgage.initial", terms.initial], ["listings.mortgage.monthly", terms.monthly], ["listings.mortgage.remaining", terms.remaining]]
      ).filter((x): x is [TranslationKey, number] => typeof x[1] === "number" && x[1] > 0)
    : [])];
  const termsLine = termPairs.length
    ? termPairs.map(([k, v]) => `${t(k)} ${formatPrice(v)} AZN`).join(" · ")
    : takeover ? t("listings.mortgage.no_terms") : "";
  // One horizontal row on wider grid cards (owner, 2026-10-16: "squeeze
  // in some horizontals"): the two sums compacted, the unit once, then
  // the short freshness.
  const termsShort = termPairs.length
    ? `${termPairs.slice(0, 2).map(([k, v]) => `${t(k)} ${compactAzn(v)}`).join(" · ")} AZN`
    : takeover ? t("listings.mortgage.no_terms") : "";
  const recencyShort = recencyLabel(first_seen_at, t, true);
  const ratePct = mortgage_kind === "takeover" && mortgage_terms?.rate_pct ? mortgage_terms.rate_pct : null;
  const rateTip = ratePct != null ? t("listings.mortgage.rate.tooltip").replace("{n}", String(ratePct)) : "";
  const deedLabel = t("listings.card.kupcha");
  // The text says the deed comes later (the deed reader's phrase, or Jev's
  // reading, lib/text-facts): the amber mark, never the deed mark.
  const deedLaterText = !deedConflict && deedLaterFromText(listing.text_facts);
  const hasDeed = has_bill_of_sale === true && !deedLaterText;
  const deedConflictLabel = deedConflict
    ? t("deed_claim.chip")
    : deedLaterText ? `${t("listings.fact.deed_later")} (${t("listings.fact.from_text")})` : null;
  // What the text says, as grey labels (2026-10-29): two at most, from
  // the owner and the papers first, then what the search asked for (the
  // rest wait in the drawer).
  // The quiet line opens with the card's one label (two on a wide card,
  // 2026-10-29): the deed risk first (amber), then the seller (from the
  // owner, or possibly an agent), then what the text says; a fact the
  // search asked for is not repeated (the applied chips say it).
  const deedRisk = deedConflict
    ? t("deed_claim.chip").replace(/^./, (c) => c.toLocaleUpperCase(locale))
    : deedLaterText ? t("listings.fact.deed_later") : null;
  const textOwner = !!listing.text_facts?.includes("owner") && !activeFacts.includes("owner");
  const quietLabels: QuietLabel[] = [
    deedRisk && { text: deedRisk, warn: true },
    textOwner ? { text: t("listings.fact.owner"), warn: false }
      : maybeAgent(listing) ? { text: t("listings.card.maybe_agent"), warn: false } : null,
    ...cardLabels(listing.text_facts)
      .filter((id) => id !== "owner" && !activeFacts.includes(id))
      .map((id) => ({ text: t(CARD_LABEL[id]), warn: false })),
  ].filter((x): x is QuietLabel => !!x).slice(0, 2);

  // The whole card is the click target - opens the live detail drawer /
  // bottom sheet. role=button + keyboard handlers keep it accessible.
  function handleOpen() {
    onOpen(listing);
  }

  // The card whose details are open is outlined in ink, not tinted
  // indigo: a tinted card read as AI decoration (owner, 2026-10-21).
  return (
    <article
      role="button"
      tabIndex={0}
      {...recData({ rec, pos })}
      onClick={handleOpen}
      // Intent: fetch the drawer's content code before the tap lands.
      onPointerEnter={preloadDetailBody}
      onFocus={preloadDetailBody}
      onTouchStart={preloadDetailBody}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpen();
        }
      }}
      aria-label={t("listings.detail.open_card")}
      className={`group flex h-full ${variant === "row" ? "flex-row" : "flex-col"} rounded-xl border overflow-hidden cursor-pointer ${active ? `${CURRENT_CARD} bg-white` : "border-gray-200 bg-white hover:border-gray-300"} focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 transition-all duration-200`}
      aria-current={active ? "true" : undefined}
    >
      {/* Thumbnail. Row variant: fixed-width column, photo fills the
          row's height. */}
      <div className={`relative bg-gray-100 overflow-hidden ${
        variant === "row"
          ? `w-40 shrink-0 min-h-40 self-stretch ${dense ? "sm:w-44" : "sm:w-60 lg:w-72"}`
          : "@container aspect-[4/3]"
      }`}>
        {cardImage ? (
          <CardPhoto
            url={shownPhoto}
            sizes={variant === "row" ? CARD_SIZES.row : CARD_SIZES.grid}
            alt={photoAlt || locationLabel}
            className="group-hover:scale-[1.02] transition-transform duration-500"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : undefined}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Building2 className="w-10 h-10 text-gray-300" />
          </div>
        )}
        {/* The photo is a real link like the price: it opens the listing
            before the page has hydrated (the card's own click handler
            only works after), for crawlers, and in a new tab on a
            modified click. A plain click still opens the drawer. */}
        {href && (
          <a
            href={href}
            tabIndex={-1}
            className="absolute inset-0"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
                e.stopPropagation();
                return;
              }
              e.preventDefault();
            }}
          >
            {/* Text for crawlers and screen readers: an audit counted
                every card's photo link as "no anchor text". */}
            <span className="sr-only">{factsLine}</span>
          </a>
        )}

        {/* Verdict chip leads (top-left, per the board) - the model's
            read is what makes this page ours. In the row variant it
            moves beside the price instead. */}
        {/* The top of a grid card: the verdict chip leads on the left (the
            model's read is what makes this page ours), the transaction,
            viewed and copies pills stack on the right. One row, so on a
            phone's narrow card the verdict gives way instead of running
            under them (owner, 2026-10-30). The row variant keeps its own
            places below. */}
        {variant === "grid" && (hasVerdict || showTransaction || seen || (dup_group_size != null && dup_group_size > 1)) && (
          <div className="absolute inset-x-2 top-2 flex items-start justify-between gap-1.5">
            <div className="flex min-w-0">
              {hasVerdict && (
                <VerdictBadge
                  tier={ai_verdict_tier}
                  pct={ai_overpriced_pct}
                  fairLabel={t("listings.card.verdict.fair")}
                  dealLabel={t("listings.card.verdict.deal")}
                  overLabel={t("listings.card.verdict.over")}
                  luxLabel={t("verdict.luxury")}
                  dealShort={t("listings.card.verdict.deal_short")}
                  overShort={t("listings.card.verdict.over_short")}
                />
              )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              {showTransaction && transactionChip}
              {seenChip}
              {dupChip}
            </div>
          </div>
        )}

        {/* Row variant: the transaction badge on the left, the viewed eye
            on the right. */}
        {variant === "row" && showTransaction && (
          <div className="absolute top-2 left-2">{transactionChip}</div>
        )}
        {variant === "row" && seenChip && (
          <div className="absolute right-2 top-2">{seenChip}</div>
        )}


        {/* Same-unit duplicate group on a row card: this card is the
            cheapest of N postings of the same unit (the rest are
            collapsed); the detail panel lists them. Stacked above the
            source-host chip (which owns the bottom-right corner), with
            the flat's sellers pill above it. */}
        {variant === "row" && ((dup_group_size != null && dup_group_size > 1) || sellersChip) && (
          <div className="absolute bottom-9 right-2 flex max-w-[calc(100%-1rem)] flex-col items-end gap-1">
            {sellersChip}
            {dup_group_size != null && dup_group_size > 1 && (
              <span
                title={t("listings.card.dup.tooltip").replace("{n}", String(dup_group_size))}
                // Warning-toned (solid amber, like the fact-check warnings):
                // "several postings of this unit" is a caution signal the
                // buyer should notice, not neutral metadata.
                className="inline-flex items-center gap-1 rounded-full bg-[#d97706] px-2 py-0.5 text-xs font-bold text-white shadow-sm"
              >
                <CopyIcon className="w-3 h-3" />
                {t("listings.card.dup.badge").replace("{n}", String(dup_group_size))}
              </span>
            )}
          </div>
        )}

        {/* Save heart (bottom-left) - for everyone. Signed out, the
            heart is device-bound (push subscription as identity, see
            use-watchlist); the ladder to an account lives in the hook.
            Heart in rose is THE saved language everywhere (owner call
            2026-07-18: one icon, red, replacing the bookmark/heart
            split). Saving (not unsaving) celebrates: spring-pop +
            particle burst + benefit chip + a haptic tick where
            supported. */}
        {!hideSave && (
          <button
            type="button"
            aria-label={watched ? t("watchlist.remove") : t("watchlist.add")}
            onClick={(e) => {
              e.stopPropagation();
              if (!watched) {
                setSaveBurst(Date.now());
                haptic(12);
              }
              void toggle(listing.id);
            }}
            className="group/heart absolute bottom-0.5 left-0.5 inline-flex h-10 w-10 items-center justify-center"
          >
            {/* The disc stays 28 px; the button around it is the 40 px
                target a thumb needs. */}
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow-sm ring-1 ring-gray-900/10 backdrop-blur-sm transition-colors group-hover/heart:bg-white">
              <Heart
                key={saveBurst}
                className={`w-3.5 h-3.5 ${saveBurst ? "save-pop" : ""} ${
                  watched ? "fill-rose-500 text-rose-500" : "text-gray-500"
                }`}
              />
            </span>
            <SaveCelebration burstKey={saveBurst} color="#f43f5e" praise={t("watchlist.praise")} />
          </button>
        )}
        {menu && <div className="absolute bottom-0.5 right-0.5">{menu}</div>}

        {/* The source site is named in the drawer and on the listing page,
            not on the card (owner, 2026-10-11: the host pill fought the
            watchlist's controls). */}
        {/* First-party chip (bottom-right): scraped cards carry their
            source host there, so an unmarked corner would read as "ours
            by omission". The solid indigo brand chip makes it explicit
            - same visual weight as the transaction chip top-left. */}
        {/* The loan's rate on a take-over (grid cards; the row's chip
            carries it): a small mark with its meaning on hover (owner,
            2026-10-15: "when you hover over the 8% it should say that
            it is the mortgage percentage"). */}
        {(variant === "grid" && (ratePct != null || sellersChip)) || listing.source === "own" ? (
          <div className={`absolute bottom-2 ${menu ? "right-10" : "right-2"} flex items-center gap-1`}>
            {variant === "grid" && sellersChip}
            {variant === "grid" && ratePct != null && (
              <span
                title={rateTip}
                aria-label={rateTip}
                className="inline-flex items-center rounded-full bg-[#111827]/85 px-2 py-0.5 text-xs font-bold text-white shadow-sm"
              >
                {ratePct}%
              </span>
            )}
            {listing.source === "own" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-bold text-white shadow-sm">
                <BrandMark size={10} variant="white" />
                menzil.ai
              </span>
            )}
          </div>
        ) : null}
      </div>

      {/* Card body - the board's three-line anatomy: price leads,
          facts as one dot-separated line (no icon squares), then a
          quiet AZN/m² + kupça + freshness line. */}
      <div className={`flex min-w-0 flex-1 ${variant === "row" ? "flex-col gap-2 p-4 sm:flex-row sm:items-stretch sm:gap-6 sm:px-5" : "flex-col gap-1 p-2.5"}`}>
      <div className={`flex min-w-0 flex-1 flex-col ${variant === "row" ? "gap-2" : "gap-1"}`}>
        {/* One line, and it does not wrap: the deed has to stay beside
            the PRICE (owner, 2026-09-22, twice). With flex-wrap the
            AZN/m² dropped to a second line on a narrow card and took the
            icon with it, which is how it ended up reading as part of
            the size instead of the price. The AZN/m² truncates instead. */}
        <div className={`flex min-w-0 items-center gap-x-2.5 ${variant === "row" ? "flex-wrap gap-y-1" : ""}`}>
          {/* Price and deed travel together as one unbreakable group in
              the list row: the verdict badge may wrap to the next line
              on a phone, the deed may not leave the price. */}
          <span className="flex shrink-0 items-center gap-2">
            {(() => {
              const text = price !== null ? `${formatPrice(price)} ${currencySign(currency) }` : t("common.na");
              const cls = `font-extrabold tabular-nums text-gray-900 dark:text-white ${variant === "row" ? "text-lg sm:text-xl" : "text-base"}`;
              if (!href) return <span className={cls}>{text}</span>;
              return (
                <a
                  href={href}
                  className={`${cls} focus:outline-none`}
                  tabIndex={-1}
                  onClick={(e) => {
                    // A modified click opens the page in a new tab and
                    // must not also open the drawer; a plain one falls
                    // through to the card's own handler.
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
                      e.stopPropagation();
                      return;
                    }
                    e.preventDefault();
                  }}
                >
                  {text}
                </a>
              );
            })()}
            {variant === "row" && hasDeed && (
              <span title={deedLabel} className="inline-flex shrink-0">
                <FileText className="h-4 w-4 text-emerald-600" aria-label={deedLabel} />
              </span>
            )}
            {variant === "row" && deedConflictLabel && (
              <span title={deedConflictLabel} className="inline-flex shrink-0">
                <FileWarning className="h-4 w-4 text-amber-600" aria-label={deedConflictLabel} />
              </span>
            )}
          </span>
          {hasVerdict && variant === "row" && (
            <VerdictBadge
              tier={ai_verdict_tier}
              pct={ai_overpriced_pct}
              fairLabel={t("listings.card.verdict.fair")}
              dealLabel={t("listings.card.verdict.deal")}
              overLabel={t("listings.card.verdict.over")}
              luxLabel={t("verdict.luxury")}
            />
          )}
          {variant === "grid" && ppm !== null && (
            // Phone cards are ~173 px wide: price + AZN/m² + the icon do
            // not fit, and something has to give. The AZN/m² moves to the
            // quiet line below sm and comes back beside the price when
            // there is room (owner, 2026-09-23: "let's just put the
            // price per square meter to the second row, but keep the
            // title and icon right").
            <span className="hidden min-w-0 truncate text-xs font-medium tabular-nums text-gray-500 sm:inline">
              {t(ppmKey).replace("{n}", formatPrice(ppm))}
            </span>
          )}
          {/* Grid cards are narrow, so the right edge of the price line
              still reads as "beside the price". A list row is the full
              width of the page, where ml-auto threw the icon metres
              away from it (owner, 2026-09-23: "it's really far"), so
              the row variant renders it next to the price above. */}
          {variant === "grid" && hasDeed && (
            <span title={deedLabel} className="ml-auto inline-flex shrink-0">
              <FileText className="h-4 w-4 text-emerald-600" aria-label={deedLabel} />
            </span>
          )}
          {/* The site ticks a deed the text says is not there yet: the
              warning mark where the deed mark would be (lib/deed-claim). */}
          {variant === "grid" && deedConflictLabel && (
            <span title={deedConflictLabel} className="ml-auto inline-flex shrink-0">
              <FileWarning className="h-4 w-4 text-amber-600" aria-label={deedConflictLabel} />
            </span>
          )}
        </div>

        {/* Optional since-saved note (watchlist only). */}
        {priceNote}

        {/* The affordability chip is gone (owner, 2026-09-21: "I don't
            really want to see the tag for the affordability, it's 0 per
            month, let's remove that completely"). Arriving from the budget
            calculator the price filter already says the budget, and in
            cash mode the chip printed a 0 AZN monthly payment. */}

        {factsLine && (
          <span className={`leading-snug text-gray-600 ${variant === "row" ? "text-sm sm:text-base" : "line-clamp-2 text-xs"}`}>
            {factsLine}
          </span>
        )}



        {/* Keyword search: the sentence that matched, hits marked - the
            answer to "why is this listing here" without opening it. */}
        {listing.match_snippet && listing.match_snippet.length > 0 && (
          <p className={`line-clamp-2 leading-snug text-gray-600 ${variant === "row" ? "text-sm" : "text-xs"}`}>
            {listing.match_snippet.map((seg, i) =>
              seg.hit ? (
                <mark key={i} className="rounded-sm bg-[#fde68a] px-0.5 font-semibold text-[#1f2937] dark:bg-[#5b4408] dark:text-[#fef3c7]">
                  {seg.text}
                </mark>
              ) : (
                <span key={i}>{seg.text}</span>
              ),
            )}
          </p>
        )}

        {/* Row variant: the facts that were a gray footnote become
            readable chips, and the right rail carries the numbers -
            the row is wide, so the width has to carry data. */}
        {variant === "row" && rowChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {rowChips.map((c) =>
              c.filter && onFilter ? (
                <button
                  key={c.label}
                  type="button"
                  onClick={(e) => {
                    // The card itself opens the drawer; a chip must not.
                    e.stopPropagation();
                    haptic();
                    onFilter(c.filter!);
                  }}
                  className={c.warn
                    ? "inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100"
                    : "rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-900 dark:bg-white/10"}
                >
                  {c.warn && <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />}
                  {c.label}
                </button>
              ) : (
                <span
                  key={c.label}
                  className={c.warn
                    ? "inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800"
                    : "rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-white/10"}
                >
                  {c.warn && <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />}
                  {c.label}
                </span>
              ),
            )}
          </div>
        )}

        {variant === "row" && recency && (
          <span className="mt-auto text-xs text-gray-500">{recency}</span>
        )}

        {variant === "grid" && takeover && (
          <p className="mt-1 text-xs font-medium tabular-nums text-gray-700 sm:hidden">{termsLine}</p>
        )}
        {variant === "grid" && (quietLabels.length > 0 || quietLine || ppm !== null || takeover) && (
          <QuietLine
            key={quietLabels.map((l) => l.text).join("|")}
            labels={quietLabels}
            ppm={ppm !== null ? t(ppmKey).replace("{n}", formatPrice(ppm)) : null}
            title={
              hasYield && projected_rent != null
                ? t("listings.card.yield.tooltip").replace("{rent}", formatPrice(projected_rent))
                : takeover ? [termsLine, quietLine].filter(Boolean).join(" · ") : undefined
            }
            rest={takeover ? (
              <>
                <span className="sm:hidden">{quietLine}</span>
                <span className="hidden sm:inline">
                  <span className="font-medium text-gray-700">{termsShort}</span>
                  {recencyShort ? ` · ${recencyShort}` : ""}
                </span>
              </>
            ) : quietLine || null}
          />
        )}
        {variant === "row" && termsLine && (
          <p className="mt-1 text-xs font-medium tabular-nums text-gray-700">{termsLine}</p>
        )}
      </div>

      {variant === "row" && !dense && (
        /* The model's read leads the rail: what the AI thinks the unit is
           worth, then the per-m² and yield facts as one quiet line. */
        <div className="hidden shrink-0 flex-col justify-center sm:flex sm:w-56 lg:w-64 sm:border-l sm:border-gray-100 sm:pl-6">
          {ai_predicted_price !== null ? (
            <>
              <span className="text-xs font-medium text-gray-500">{t("listings.row.ai_value_label")}</span>
              <span className="mt-0.5 text-xl font-extrabold leading-tight tabular-nums text-gray-900 dark:text-white">
                {formatPrice(roundEstimate(ai_predicted_price, leased === true))} <span className="text-sm font-semibold text-gray-500">AZN</span>
              </span>
            </>
          ) : ppm !== null ? (
            <>
              <span className="text-xs font-medium text-gray-500">{t(isLand ? "listings.row.pps_label" : "listings.row.ppm_label")}</span>
              <span className="mt-0.5 text-xl font-extrabold leading-tight tabular-nums text-gray-900 dark:text-white">
                {formatPrice(ppm)} <span className="text-sm font-semibold text-gray-500">{ppmUnit}</span>
              </span>
            </>
          ) : null}
          <span className="mt-2 text-xs tabular-nums text-gray-500">
            {[
              ai_predicted_price !== null && ppm !== null && t(ppmKey).replace("{n}", formatPrice(ppm)),
              hasYield && `${t("listings.card.yield")} ${Math.round(gross_yield_pct!)}%`,
              photos_count !== null && photos_count > 0 && `${photos_count} ${t("listings.row.photos_label").toLowerCase()}`,
            ].filter(Boolean).join(" · ")}
          </span>
        </div>
      )}
      </div>
    </article>
  );
}
